import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { performComprehensiveSearch } from '../../utils/searchUtils';
import './SearchBar.css';

const SearchBar = ({ onSearchPage = false }) => {
  const [searchParams] = useSearchParams();
  const initialQuery = onSearchPage ? (searchParams.get('q') || '') : '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isExpanded, setIsExpanded] = useState(onSearchPage);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Update query when search params change (for search page)
  useEffect(() => {
    if (onSearchPage) {
      const query = searchParams.get('q') || '';
      setSearchQuery(query);
      setIsExpanded(true);
    }
  }, [searchParams, onSearchPage]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current && !onSearchPage) {
      inputRef.current.focus();
    }
  }, [isExpanded, onSearchPage]);

  // Perform search
  const performSearch = (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const results = performComprehensiveSearch(query);
    setSearchResults(results.slice(0, 5)); // Show top 5 in dropdown
    setShowResults(true); // Always show dropdown when 2+ chars (results or "no results")
  };

  // Handle icon click - expand search
  const handleIconClick = () => {
    setIsExpanded(true);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!onSearchPage) {
      performSearch(value);
    }
  };

  // Handle search submit
  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery.trim().length >= 2) {
      setShowResults(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  // Handle result click
  const handleResultClick = (path) => {
    setSearchQuery('');
    setShowResults(false);
    setIsExpanded(false);
    navigate(path);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    } else if (e.key === 'Escape') {
      if (!onSearchPage) {
        setIsExpanded(false);
        setSearchQuery('');
        setShowResults(false);
      } else {
        setShowResults(false);
      }
    }
  };

  // Close results and collapse when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        if (!onSearchPage && isExpanded && !searchQuery.trim()) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, searchQuery, onSearchPage]);

  return (
    <div 
      className={`search-bar-container ${onSearchPage ? 'search-page-mode' : ''} ${isExpanded ? 'expanded' : 'collapsed'}`} 
      ref={searchRef}
    >
      {/* Form first, icon second - so icon stays on the right and doesn't jump on close */}
      <form 
        onSubmit={handleSearch} 
        className={`search-input-wrapper ${isExpanded ? 'expanded' : ''}`}
      >
        <div className="search-icon-left">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder={onSearchPage ? "Search Salaam Takaful..." : "Search website..."}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => {
            if (!onSearchPage) {
              setIsExpanded(true);
            }
            if (searchQuery.trim().length >= 2) {
              performSearch(searchQuery);
            }
          }}
          onKeyDown={handleKeyDown}
          autoFocus={onSearchPage}
        />
        <button className="search-button" type="submit" aria-label="Search">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </button>
        {!onSearchPage && isExpanded && (
          <button 
            className="search-close-button"
            type="button"
            onClick={() => {
              setIsExpanded(false);
              setSearchQuery('');
              setShowResults(false);
            }}
            aria-label="Close search"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </form>

      {!onSearchPage && (
        <button 
          className="search-icon-button"
          onClick={handleIconClick}
          type="button"
          aria-label="Search"
          aria-hidden={isExpanded}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </button>
      )}

      {!onSearchPage && isExpanded && showResults && searchResults.length > 0 && (
        <div className="search-results-dropdown">
          <div className="search-results-header">
            <span>Quick Results ({searchResults.length})</span>
            {searchQuery.trim().length >= 2 && (
              <button 
                className="view-all-results-btn"
                onClick={() => handleSearch()}
              >
                View All Results →
              </button>
            )}
          </div>
          <div className="search-results-list">
            {searchResults.map((result, index) => (
              <div
                key={index}
                className="search-result-item"
                onClick={() => handleResultClick(result.path)}
              >
                <div className="search-result-icon">
                  {result.type === 'page' && '📄'}
                  {result.type === 'news' && '📰'}
                  {result.type === 'article' && '📝'}
                  {result.type === 'product' && '🛡️'}
                </div>
                <div className="search-result-content">
                  <div className="search-result-title">{result.title}</div>
                  <div className="search-result-category">{result.category}</div>
                  {result.snippet && (
                    <div className="search-result-description">
                      {result.snippet.length > 80
                        ? `${result.snippet.substring(0, 80)}...`
                        : result.snippet}
                    </div>
                  )}
                </div>
                <div className="search-result-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!onSearchPage && isExpanded && showResults && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
        <div className="search-results-dropdown">
          <div className="search-no-results">
            <span>No quick results found</span>
            <button 
              className="view-all-results-btn"
              onClick={() => handleSearch()}
            >
              Search All Content →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
